import React, {
  createContext,
  type ReactNode,
  useMemo,
  useReducer,
} from 'react';

type UploadImageModalState = {
  cropImage: boolean;
  base64Image?: string | null;
  imageUrl?: string | null;
  enableCrop: boolean;
  userCropInteraction: boolean;
  base64CroppedImage?: string | null;
  removeImage?: boolean;
};

type UploadImageModalAction =
  | { type: 'TOGGLE_CROP_IMAGE'; payload: boolean }
  | { type: 'SET_BASE64_IMAGE'; payload: string | null }
  | { type: 'SET_BASE64_CROPPED_IMAGE'; payload: string | null }
  | { type: 'IMAGE_URL'; payload?: string | null }
  | { type: 'ENABLE_CROP'; payload: boolean }
  | { type: 'USER_CROP_INTERACTION'; payload: boolean }
  | { type: 'REMOVE_IMAGE'; payload?: boolean }
  | { type: 'RESET_MODAL' };

const initialState: UploadImageModalState = {
  cropImage: false,
  userCropInteraction: false,
  enableCrop: false,
  base64Image: null,
  base64CroppedImage: null,
};

const reducer = (
  state: UploadImageModalState,
  action: UploadImageModalAction,
): UploadImageModalState => {
  switch (action.type) {
    case 'TOGGLE_CROP_IMAGE':
      return {
        ...state,
        cropImage: action.payload,
      };
    case 'SET_BASE64_IMAGE': // sets the base64 image
      return {
        ...state,
        base64Image: action.payload,
      };
    case 'SET_BASE64_CROPPED_IMAGE': // sets the base64 cropped image
      return {
        ...state,
        base64CroppedImage: action.payload,
      };
    case 'IMAGE_URL': // sets the image url if there is one when the modal is opened
      return {
        ...state,
        imageUrl: action.payload,
      };
    case 'ENABLE_CROP': // enables the crop photo control
      return {
        ...state,
        enableCrop: action.payload,
      };
    case 'USER_CROP_INTERACTION': // changes to true if the user has interacted with the cropping feature by zooming or moving the image
      return {
        ...state,
        userCropInteraction: action.payload,
      };
    case 'REMOVE_IMAGE':
      return { ...initialState };
    case 'RESET_MODAL':
      return {
        ...initialState,
        imageUrl: state.imageUrl,
      };
    default:
      return state;
  }
};

export const UploadImageModalContext = createContext<
  [UploadImageModalState, React.Dispatch<UploadImageModalAction>]
>([initialState, () => initialState]);

export const UploadImageModalProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const value: [UploadImageModalState, React.Dispatch<UploadImageModalAction>] =
    useMemo(() => [state, dispatch], [state]);

  return (
    <UploadImageModalContext.Provider value={value}>
      {children}
    </UploadImageModalContext.Provider>
  );
};
